<template>
  <div class="editor-wrap">
    <div class="editor-wrap__body">
      <div class="name-input-wrap">
        <div>
          <span class="sidebar-helptext">
            {{ $t('model.nlu-model.sidebar.flow_entities.flow_entity_editor.refreshFlowList') }}
          </span>
          <or-icon-button
            icon="refresh"
            size="small"
            type="secondary"
            class="flat"
            :loading="isRefreshingFlows"
            @click="refreshFlowList"
          ></or-icon-button>
        </div>
        <or-textbox
          v-model="tempEntity.name"
          :label="$t('model.nlu-model.sidebar.flow_entities.flow_entity_editor.flowEntityName')"
          :maxlength="22"
        ></or-textbox>
      </div>
      <div v-if="renderFlowEditors" class="flows-list">
        <flow-editor
          v-for="(flow, index) in tempEntity.flows"
          :readonly="isLoading"
          :key="index"
          :index="index"
          :flow="flow"
          :flows-amount="tempEntity.flows.length"
          @update="onUpdateFlow"
          @delete="onDeleteFlow"
        />
      </div>
      <div class="add-btn-wrap">
        <or-button
          v-if="showAddFlowBtn"
          class="flat add-btn"
          type="secondary"
          icon="add"
          size="small"
          color="primary"
          :disabled="isLoading"
          @click="onAddFlow"
        >
          {{ $t('model.nlu-model.sidebar.flow_entities.flow_entity_editor.addFlow') }}
        </or-button>

        <p v-else>
          {{ $t('model.nlu-model.sidebar.flow_entities.flow_entity_editor.youCouldAdd') }}
        </p>
      </div>
    </div>

    <div class="footer">
      <or-button
        type="secondary"
        color="primary"
        class="cancel-btn"
        :disabled="isLoading"
        @click="onCancel"
      >
        {{ $t('generic.cancel') }}
      </or-button>
      <or-button
        color="primary"
        :disabled="!createIsDisabled"
        :loading="isLoading"
        @click="isEditMode ? onUpdate() : onCreate()"
      >
        {{ $t(isEditMode ? 'generic.update' : 'generic.create') }}
      </or-button>
    </div>
  </div>
</template>

<script>
import flowEditor from './flow_editor'
import { entitiesLimits } from '@/modules/limits'
import { mapState, mapActions, mapGetters } from 'vuex'
import KnowledgeBus from '@/KnowledgeBus'

export default {
  components: { flowEditor },

  created () {
    KnowledgeBus.$on('temp-flow-entity::set', this.setTempEntity)
  },

  beforeDestroy () {
    KnowledgeBus.$off('temp-flow-entity::set', this.setTempEntity)
  },

  computed: {
    ...mapState('knowledgebase/entities', ['postprocessEnities']),
    ...mapGetters('knowledgebase/flows', ['nluFlows']),

    nameIsValid () {
      const postprocessEnitiesNames = _.chain(this.postprocessEnities.value)
        .map('name')
        .reject((el) => el === _.trim(this.originalEntity.name))
        .value()
      const nameNotExist = !_.includes(
        postprocessEnitiesNames,
        _.trim(this.tempEntity.name)
      )
      const nameNotEmpty = !_.isEmpty(_.trim(this.tempEntity.name))

      return nameNotEmpty && nameNotExist
    },

    flowsIsValid () {
      return _.every(this.tempEntity.flows, (flow) => !_.isEmpty(flow.label))
    },

    hasChanges () {
      return !_.isEqual(this.tempEntity, this.originalEntity)
    },

    createIsDisabled () {
      return this.hasChanges && this.flowsIsValid && this.nameIsValid
    },

    showAddFlowBtn () {
      return (
        this.tempEntity.flows.length < entitiesLimits.flow.max_chain_length
      )
    }
  },

  data () {
    return {
      tempEntity: {
        name: '',
        flows: [{}]
      },
      originalEntity: {
        name: '',
        flows: [{}]
      },
      renderFlowEditors: true,
      isEditMode: false,
      isLoading: false,
      isRefreshingFlows: false
    }
  },

  methods: {
    ...mapActions('knowledgebase', [
      'createEntity',
      'updateEntity',
      'fetchActiveDeployments',
      'fetchFlows'
    ]),

    async refreshFlowList () {
      this.isRefreshingFlows = true
      await Promise.all([this.fetchActiveDeployments(), this.fetchFlows()])
      this.isRefreshingFlows = false
    },

    onUpdateFlow (updatedFlow) {
      this.$set(this.tempEntity.flows, updatedFlow.index, updatedFlow.value)
    },

    setTempEntity (flowEntity) {
      // hack: trigger Created() hook in flow_editor
      this.renderFlowEditors = false

      this.isEditMode = true

      const editableEntityName = _.clone(flowEntity.name)
      const editableEntityFlows = _.cloneDeep(flowEntity.processFlow)

      editableEntityFlows.forEach((el) => {
        const flowParameters = _.chain(this.nluFlows.value)
          .find(['id', el.id])
          .get('params')
          .map('name')
          .mapKeys((v) => v)
          .mapValues((v) => undefined)
          .value()
        el.params = _.assign(
          flowParameters,
          _.pick(el.params, _.keys(flowParameters))
        )
      })
      this.$set(this.tempEntity, 'id', flowEntity.id)
      this.$set(this.tempEntity, 'name', editableEntityName)
      this.$set(this.tempEntity, 'flows', editableEntityFlows)
      this.$set(this.originalEntity, 'id', flowEntity.id)

      this.$set(this.originalEntity, 'name', _.cloneDeep(editableEntityName))
      this.$set(this.originalEntity, 'flows', _.cloneDeep(editableEntityFlows))

      this.$nextTick(() => {
        this.renderFlowEditors = true
      })
    },

    onAddFlow () {
      this.tempEntity.flows.push({})
    },

    onDeleteFlow (flowIndex) {
      // hack: trigger Created() hook in flow_editor
      this.renderFlowEditors = false

      const flows = _.filter(
        this.tempEntity.flows,
        (value, index) => index !== flowIndex
      )
      this.$set(this.tempEntity, 'flows', flows)

      this.$nextTick(() => {
        this.renderFlowEditors = true
      })
    },

    async onCreate () {
      this.isLoading = true

      await this.createEntity({
        type: 'postprocess',
        name: this.tempEntity.name,
        processFlow: this.tempEntity.flows
      })

      this.isLoading = false
      this.$emit('back')
    },

    async onUpdate () {
      this.isLoading = true

      const flowEntityId = _.chain(this.postprocessEnities.value)
        .find(['id', this.originalEntity.id])
        .get('id')
        .value()

      await this.updateEntity({
        id: flowEntityId,
        type: 'postprocess',
        name: this.tempEntity.name,
        processFlow: this.tempEntity.flows
      })
      this.$emit('back')

      this.isLoading = false
    },

    onCancel () {
      this.$emit('back')
    }
  },

  watch: {
    tempEntity: {
      handler (value) {
        console.log('tempEntity: ', value)
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-wrap {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  max-height: 100%;

  &__body {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 16px;

    max-height: 100%;
    overflow: auto;
    overflow-y: scroll;

    .name-input-wrap {
      // height: 120px;
      margin-bottom: 1rem;
    }

    .flows-list {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      overflow: auto;
      overflow-x: scroll;
    }

    .add-btn-wrap {
      display: flex;
      align-items: left;

      .add-btn {
        padding: 0;
        outline: none;
      }
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: $sp-3;
    border-top: solid 1px $or-grey-medium;

    .cancel-btn {
      margin-right: $sp-3;
    }
  }
}
</style>
